import React, { useCallback, useEffect, useState } from "react";
import { MapPin, Search, X } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { getUserCoords } from "@/lib/location";
import { PageHeader, FullPageSpinner, EmptyState } from "@/components/kit";
import VetCard from "@/components/VetCard";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "featured", label: "Featured" },
  { key: "emergency", label: "24/7 Emergency" },
];

export default function Vets() {
  const [vets, setVets] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [coords, setCoords] = useState(null);
  const [locTried, setLocTried] = useState(false);
  const [zip, setZip] = useState("");
  const [zipInput, setZipInput] = useState("");
  const [showZip, setShowZip] = useState(false);

  useEffect(() => {
    getUserCoords().then((c) => {
      setCoords(c);
      setLocTried(true);
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter === "featured") params.set("featured", "true");
      if (filter === "emergency") params.set("emergency", "true");
      if (zip) {
        params.set("zip", zip);
      } else if (coords) {
        params.set("lat", coords.lat);
        params.set("lng", coords.lng);
      }
      const qs = params.toString();
      const data = await apiFetch(`/vets${qs ? `?${qs}` : ""}`, {}, false);
      setVets(data || []);
    } catch {
      setVets([]);
    } finally {
      setLoading(false);
    }
  }, [filter, coords, zip]);

  useEffect(() => {
    load();
  }, [load]);

  const submitZip = (e) => {
    e.preventDefault();
    const z = zipInput.trim();
    if (z.length < 3) return;
    setZip(z);
  };

  const clearZip = () => {
    setZip("");
    setZipInput("");
    setShowZip(false);
  };

  const subtitle = zip
    ? `Showing vets near ${zip}.`
    : coords
    ? "Sorted by distance from your location."
    : "Clinics across Portland, Eugene & San Francisco.";

  // Show the ZIP box when: user opened it, or geolocation finished and wasn't available.
  const zipVisible = showZip || zip || (locTried && !coords);

  return (
    <div>
      <PageHeader title="Find a vet" subtitle={subtitle} />

      {/* Location control */}
      <div className="mb-5">
        {zipVisible ? (
          <form onSubmit={submitZip} className="flex items-center gap-2" data-testid="vet-zip-form">
            <div className="relative flex-1 max-w-xs">
              <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A938B]" />
              <input
                data-testid="vet-zip-input"
                value={zipInput}
                onChange={(e) => setZipInput(e.target.value)}
                inputMode="numeric"
                maxLength={10}
                placeholder="Enter ZIP code"
                className="w-full pl-9 pr-3 py-2.5 rounded-full bg-white border border-[#E8E3DA] text-[#2A2724] font-semibold text-sm focus:ring-2 focus:ring-[#E86A45] outline-none"
              />
            </div>
            <button
              type="submit"
              data-testid="vet-zip-submit"
              className="flex items-center gap-1.5 rounded-full bg-[#E86A45] text-white font-bold text-sm px-4 py-2.5 hover:bg-[#d65f3c] transition-colors"
            >
              <Search size={15} /> Search
            </button>
            {(zip || coords) && (
              <button type="button" onClick={clearZip} data-testid="vet-zip-clear" className="text-sm font-bold text-[#6B6560] px-2 py-2.5 flex items-center gap-1">
                <X size={15} /> {coords ? "Use my location" : "Clear"}
              </button>
            )}
          </form>
        ) : (
          <button
            type="button"
            data-testid="vet-use-zip-btn"
            onClick={() => setShowZip(true)}
            className="text-sm font-bold text-[#6F8F72] flex items-center gap-1.5"
          >
            <MapPin size={15} /> Location off? Search by ZIP code instead
          </button>
        )}
        {locTried && !coords && !zip && (
          <p className="text-xs text-[#9A938B] mt-2">
            We couldn&apos;t access your location. Enter a ZIP code to find nearby vets.
          </p>
        )}
      </div>

      <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`vet-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-bold transition-colors ${
              filter === f.key ? "bg-[#E86A45] text-white" : "bg-[#F2EFE9] text-[#4A4746] hover:bg-[#E8E3DA]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <FullPageSpinner />
      ) : vets.length === 0 ? (
        <EmptyState icon={MapPin} title="No vets found" body="Try a different filter or ZIP code." />
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {vets.map((v) => (
            <VetCard key={v.id} vet={v} />
          ))}
        </div>
      )}
    </div>
  );
}
